import { useEffect, useState } from "react";
import { CloudDownload, Download, HardDrive, Smartphone, Trash2, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cacheNow, clearOfflineCopy, subscribeOffline } from "@/lib/offline/sync";
import { useInstallPrompt, showInstallHelp } from "@/components/InstallPrompt";

const MONTHS_BACK = [1, 2, 3, 6, 12];
const MONTHS_AHEAD = [3, 6, 9, 12];
const ATTACH_DAYS = [0, 7, 21, 30];

const mb = (bytes) => `${Math.round((bytes || 0) / 1048576)} MB`;

/** Owner control over what the iPad keeps for courtroom use. */
export const OfflineSettings = ({ prefs, onSave }) => {
  const [state, setState] = useState(null);
  const [storage, setStorage] = useState(null);
  const { canInstall, install, installed } = useInstallPrompt();

  useEffect(() => subscribeOffline(setState), []);

  useEffect(() => {
    if (navigator.storage?.estimate) {
      navigator.storage.estimate().then(setStorage).catch(() => {});
    }
  }, [state?.cachedAt]);

  const enabled = prefs.offline_enabled !== false;
  const back = Number(prefs.offline_months_back ?? 3);
  const ahead = Number(prefs.offline_months_ahead ?? 6);
  const attach = Number(prefs.offline_attachment_days ?? 21);

  return (
    <section className="surface p-6 lg:p-8" data-testid="offline-settings">
      <h2 className="text-base md:text-lg font-heading font-semibold inline-flex items-center gap-2">
        <WifiOff className="h-5 w-5" /> Works without a signal
      </h2>
      <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
        Add the diary to your iPad home screen and it keeps a copy of this window of dates on the
        device. In court you can still read everything and add or edit entries, hearing logs, to-dos
        and post-its — they reach the diary the moment you have a signal again.
      </p>

      <div className="mt-6 flex items-center justify-between gap-4 border border-border rounded-sm px-4 py-3">
        <div>
          <Label className="text-base font-semibold">Keep a copy on this device</Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            Refreshes quietly in the background whenever you're online
          </p>
        </div>
        <Switch
          data-testid="offline-enabled-switch"
          checked={enabled}
          onCheckedChange={(v) => onSave({ ...prefs, offline_enabled: v })}
        />
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label className="label-caps">How far back</Label>
          <Select
            value={String(back)}
            onValueChange={(v) => onSave({ ...prefs, offline_months_back: Number(v) })}
          >
            <SelectTrigger data-testid="offline-months-back" className="mt-2 h-12 rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS_BACK.map((m) => (
                <SelectItem key={m} value={String(m)}>
                  {m} month{m === 1 ? "" : "s"} of past dates
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-caps">How far ahead</Label>
          <Select
            value={String(ahead)}
            onValueChange={(v) => onSave({ ...prefs, offline_months_ahead: Number(v) })}
          >
            <SelectTrigger data-testid="offline-months-ahead" className="mt-2 h-12 rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS_AHEAD.map((m) => (
                <SelectItem key={m} value={String(m)}>
                  {m} months of listings
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-caps">Orders and files</Label>
          <Select
            value={String(attach)}
            onValueChange={(v) => onSave({ ...prefs, offline_attachment_days: Number(v) })}
          >
            <SelectTrigger data-testid="offline-attachment-days" className="mt-2 h-12 rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ATTACH_DAYS.map((d) => (
                <SelectItem key={d} value={String(d)}>
                  {d === 0 ? "Text only, no files" : `Files for hearings within ${d} days`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <dl className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
        <div>
          <dt className="label-caps">Entries held</dt>
          <dd className="mt-1 text-lg font-semibold" data-testid="offline-settings-events">
            {state?.counts?.events ?? 0}
          </dd>
        </div>
        <div>
          <dt className="label-caps">Matters</dt>
          <dd className="mt-1 text-lg font-semibold">{state?.counts?.matters ?? 0}</dd>
        </div>
        <div>
          <dt className="label-caps">Last cached</dt>
          <dd className="mt-1 text-sm font-semibold">
            {state?.cachedAt ? new Date(state.cachedAt).toLocaleString() : "Never"}
          </dd>
        </div>
        <div>
          <dt className="label-caps inline-flex items-center gap-1.5">
            <HardDrive className="h-3.5 w-3.5" /> On device
          </dt>
          <dd className="mt-1 text-sm font-semibold">{storage ? mb(storage.usage) : "—"}</dd>
        </div>
      </dl>

      {state?.progress ? (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="offline-progress">
          Caching {state.progress.step}… ({state.progress.done}/{state.progress.total})
        </p>
      ) : null}

      <div className="mt-6 flex flex-wrap gap-3">
        <Button
          data-testid="offline-cache-btn"
          disabled={state?.caching}
          onClick={() => cacheNow(prefs)}
          className="rounded-full h-12 px-6"
        >
          <CloudDownload className="h-5 w-5 mr-2" />
          {state?.caching ? "Caching…" : "Save the diary to this device now"}
        </Button>
        <Button
          variant="outline"
          data-testid="offline-clear-btn"
          onClick={clearOfflineCopy}
          className="rounded-full h-12 px-5"
        >
          <Trash2 className="h-5 w-5 mr-2" /> Clear the offline copy
        </Button>
        {canInstall && !installed ? (
          <Button
            variant="outline"
            data-testid="settings-install-btn"
            onClick={install}
            className="rounded-full h-12 px-5"
          >
            <Download className="h-5 w-5 mr-2" /> Install as an app
          </Button>
        ) : null}
        {installed ? null : (
          <Button
            variant="outline"
            data-testid="settings-install-help-btn"
            onClick={showInstallHelp}
            className="rounded-full h-12 px-5"
          >
            <Smartphone className="h-5 w-5 mr-2" /> Show me how to add it to the home screen
          </Button>
        )}
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        On the iPad: open the diary in Safari, tap Share, then <strong>Add to Home Screen</strong>. On
        Android: open it in Chrome and tap <strong>Install app</strong>
        {installed ? " — this device already runs it as an app" : ""}. Either way it opens
        full-screen, with this copy behind it.
      </p>
    </section>
  );
};
